import React, { useState, useCallback, useMemo } from 'react';

import { Button } from '@epoch-ui/components/Button';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { cn } from '@epoch-ui/lib/utils';
import { Icon } from '@epoch-ui/components/Icon';

import { COMMUNICATION_REMINDER_MEDIUM } from '../types/communication.types';
import { canManagePostEventReminders } from '../utils/postEventReminderWindow';

import { ReminderListCard } from './ReminderListCard';
import { UpdateEventCommunicationSideSheet } from './EditEventCommunicationSideSheet/UpdateEventCommunicationSideSheet';

interface CommunicationRemindersListProps {
  openCreateEventCommunicationSideSheet: () => void;
}

export const CommunicationRemindersList: React.FC<CommunicationRemindersListProps> = ({
  openCreateEventCommunicationSideSheet
}) => {
  const { datetimeEnd, emailReminders, hasEnded, scheduledDeletionAt, slackReminders } =
    useEditableEvent();

  const canManageReminders = canManagePostEventReminders({
    datetimeEnd,
    hasEnded,
    scheduledDeletionAt
  });

  const [updateEventCommunicationSideSheetOpen, setUpdateEventCommunicationSideSheetOpen] =
    useState(false);

  const [selectedReminder, setSelectedReminder] = useState<any>(null);
  const [selectedMedium, setSelectedMedium] = useState<COMMUNICATION_REMINDER_MEDIUM | null>(null);

  /**
   * Safe guard when closing the side sheet.
   * Reset the selected reminder and medium.
   */
  const handleCloseUpdateEventCommunicationSideSheet = (): void => {
    setUpdateEventCommunicationSideSheetOpen(false);
    setSelectedReminder(null);
    setSelectedMedium(null);
  };

  /**
   * Merge emailReminders and slackReminders by datetime
   * Each reminder will have a datetime property
   * We'll merge and sort them by datetime ascending
   */

  const normalizeReminders = useCallback(
    (reminders: any, medium: COMMUNICATION_REMINDER_MEDIUM) =>
      (reminders || []).map((reminder: any) => ({
        ...reminder,
        title: reminder.title || reminder.subject,
        medium
      })),
    []
  );

  const mergedReminders = useMemo(() => {
    const allReminders = [
      ...normalizeReminders(emailReminders, COMMUNICATION_REMINDER_MEDIUM.EMAIL),
      ...normalizeReminders(slackReminders, COMMUNICATION_REMINDER_MEDIUM.SLACK)
    ];
    return allReminders.sort((a, b) => {
      const dateA = a.datetime ? new Date(a.datetime) : new Date(0);
      const dateB = b.datetime ? new Date(b.datetime) : new Date(0);
      return dateA.getTime() - dateB.getTime();
    });
  }, [emailReminders, slackReminders, normalizeReminders]);

  /**
   * Open the update event communication side sheet.
   * 1. Find the reminder from the merged list by id and medium.
   * 2. Set the selected reminder and medium.
   * 3. Open the side sheet.
   * @param id
   * @param medium
   */
  const handleUpdateEventCommunicationSideSheet = (
    id: number,
    medium: COMMUNICATION_REMINDER_MEDIUM
  ): void => {
    const foundReminder = mergedReminders.find(
      (reminder) => reminder.id === id && reminder.medium === medium
    );

    if (!foundReminder) return;

    setUpdateEventCommunicationSideSheetOpen(true);
    setSelectedReminder(foundReminder);
    setSelectedMedium(medium);
  };

  return (
    <>
      <div className="tw-mt-4">
        {mergedReminders.map((reminder, index) => {
          return (
            <div
              key={`${reminder.medium}-${reminder.id}`}
              className={cn(
                'tw-border tw-border-solid tw-border-gray-300 tw-p-4',
                !canManageReminders && 'tw-opacity-40',
                index === 0 && 'tw-rounded-t-lg',
                index === mergedReminders.length - 1 && 'tw-rounded-b-lg',
                index !== mergedReminders.length - 1 && 'tw-border-b-0'
              )}
            >
              <ReminderListCard
                disabled={!canManageReminders}
                normalizedReminder={reminder}
                openUpdateEventCommunicationSideSheet={(id, medium) =>
                  handleUpdateEventCommunicationSideSheet(id, medium)
                }
              />
            </div>
          );
        })}
      </div>
      <Button
        disabled={!canManageReminders}
        className="tw-mt-4 tw-flex tw-h-8 tw-items-center tw-gap-1 tw-rounded-xl"
        onClick={openCreateEventCommunicationSideSheet}
      >
        <Icon name="plus" size={16} />
        Add Reminder
      </Button>

      {/* Update Event Communication Side Sheet */}
      <UpdateEventCommunicationSideSheet
        isOpen={updateEventCommunicationSideSheetOpen}
        onClose={handleCloseUpdateEventCommunicationSideSheet}
        slackReminder={
          selectedMedium === COMMUNICATION_REMINDER_MEDIUM.SLACK ? selectedReminder : null
        }
        emailReminder={
          selectedMedium === COMMUNICATION_REMINDER_MEDIUM.EMAIL ? selectedReminder : null
        }
      />
    </>
  );
};
